import React, { useState, useRef, useEffect } from "react";

const DualBarChart = ({ data, isLoading, height = 300, barGap = 0.18 }) => {
  const [hovered, setHovered] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [chartWidth, setChartWidth] = useState(800);
  const chartRef = useRef(null);

  // Update width automatically when parent resizes
  useEffect(() => {
    if (!chartRef.current) return;
    const observer = new ResizeObserver((entries) => {
      if (entries[0]) setChartWidth(entries[0].contentRect.width);
    });
    observer.observe(chartRef.current);
    return () => observer.disconnect();
  }, []);

  const maxValue = Math.max(...data.flatMap((d) => [d.value1, d.value2]));
  const barGroupWidth = chartWidth / data.length;
  const chartHeight = height - 40;

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[200px] w-full">
        <div className="mini-spinner mt-3"></div>
      </div>
    );
  }

  if (!isLoading && data.length === 0) {
    return (
      <div className="flex items-center justify-center h-[200px] w-full">
        No data available
      </div>
    );
  }

  

  return (
    <div ref={chartRef} className="relative w-full" onMouseMove={handleMouseMove}>
      <svg
        viewBox={`0 0 ${chartWidth + 60} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        width="100%"
        height={`${height}px`}
        className="w-full overflow-visible"
      >
        {/* Left Y-axis */}
        <line
          x1="40"
          y1="10"
          x2="40"
          y2={chartHeight + 10}
          stroke="#9ca3af"
          strokeWidth="2"
        />

        {/* Y-axis labels */}
        {[0, 0.25, 0.5, 0.75, 1].map((fraction, i) => {
          const y = chartHeight - chartHeight * fraction + 10;
          const value = Math.round(maxValue * fraction);
          return (
            <text
              key={i}
              x="30"
              y={y + 12}
              textAnchor="end"
              className="text-base font-medium"
            >
              {value}
            </text>
          );
        })}

        {/* X-axis */}
        <line
          x1="40"
          y1={chartHeight + 10}
          x2={chartWidth + 40}
          y2={chartHeight + 10}
          stroke="#9ca3af"
          strokeWidth="2"
        />

        {/* Bars */}
        {data.map((d, i) => {
          const barHeight1 = (d.value1 / maxValue) * chartHeight;
          const barHeight2 = (d.value2 / maxValue) * chartHeight;
          const x = 60 + i * barGroupWidth;
          const barWidth = barGroupWidth * 0.15; // 15% width

          return (
            <g key={i}>
              {/* Bar 1 */}
              <rect
                x={x}
                y={chartHeight - barHeight1 + 10}
                width={barWidth}
                height={barHeight1}
                fill={d.color1 || "#245CEA"}
                className="cursor-pointer transition-all duration-300 hover:opacity-80"
                onMouseEnter={() =>
                  setHovered({ label: d.label, value: d.value1, color: d.color1, hoverLable: d.hoverLable1 })
                }
                onMouseLeave={() => setHovered(null)}
              />

              {/* Bar 2 */}
              <rect
                x={x + barGroupWidth * barGap}
                y={chartHeight - barHeight2 + 10}
                width={barWidth}
                height={barHeight2}
                fill={d.color2 || "#60a5fa"}
                className="cursor-pointer transition-all duration-300 hover:opacity-80"
                onMouseEnter={() =>
                  setHovered({ label: d.label, value: d.value2, color: d.color2, hoverLable: d.hoverLable2 })
                }
                onMouseLeave={() => setHovered(null)}
              />

              {/* Label */}
              <text
                x={x + barGroupWidth * 0.18}
                y={chartHeight + 30}
                textAnchor="middle"
                className="text-base font-medium"
              >
                {d.label}
              </text>
            </g>
          );
        })}
      </svg>

      {/* Tooltip that follows cursor */}
      {hovered && (
        <div
          className="absolute bg-white  text-sm px-3 py-2 rounded-lg shadow-lg pointer-events-none whitespace-nowrap"
          style={{
            top: `${mousePos.y - 35}px`,
            left: `${mousePos.x + 15}px`,
          }}
        >
          <div className="font-semibold">
            {hovered.hoverLable} : <span style={{ color: hovered.color || "#fff" }}>{hovered.value}</span>
          </div>
        </div>
      )}

      <div className="flex flex-wrap justify-center items-center gap-8 mt-[20px]">
        <div className="flex items-center space-x-[10px]">
          <div className="w-4 h-4 rounded bg-[#4179B1]"></div>
          <div className="text-[#373737] font-mont font-semibold lowercase cursor-pointer first-letter:uppercase">
            Total
          </div>
        </div>

        <div className="flex items-center space-x-[10px]">
          <div className="w-4 h-4 rounded bg-[#94C453]"></div>
          <div className="text-[#373737] font-mont font-semibold lowercase cursor-pointer first-letter:uppercase">
            Answered
          </div>
        </div>
      </div>

    </div>
  );
};

export default DualBarChart;
